/**
 * Authentication Service
 * Handles Firebase authentication operations
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  GithubAuthProvider,
  sendEmailVerification,
  applyActionCode,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User as FirebaseUser,
  type UserCredential,
} from 'firebase/auth'
import { getFirebaseAuth } from '@/config/firebase'

/**
 * Auth result containing user and token
 */
export interface AuthResult {
  user: FirebaseUser
  token: string
}

/**
 * Get Firebase ID token from user
 *
 * @param user - Firebase user
 * @param forceRefresh - Force token refresh even if not expired
 * @returns ID token string
 */
async function getToken(user: FirebaseUser, forceRefresh = false): Promise<string> {
  return await user.getIdToken(forceRefresh)
}

/**
 * Process Firebase user credential and return auth result
 *
 * @param credential - Firebase user credential
 * @returns Auth result with user and token
 */
async function processCredential(
  credential: UserCredential
): Promise<AuthResult> {
  const token = await getToken(credential.user)
  return {
    user: credential.user,
    token,
  }
}

/**
 * Login with email and password
 *
 * @param email - User email
 * @param password - User password
 * @param rememberMe - If true, persist session across browser close (default: true)
 * @returns Auth result with user and token
 * @throws Error if login fails
 */
export async function loginWithEmail(
  email: string,
  password: string,
  rememberMe: boolean = true
): Promise<AuthResult> {
  const auth = getFirebaseAuth()

  // Set persistence before signing in
  const { setAuthPersistence } = await import('@/config/firebase')
  await setAuthPersistence(rememberMe)

  const credential = await signInWithEmailAndPassword(auth, email, password)
  return processCredential(credential)
}

/**
 * Register new user with email and password
 *
 * @param email - User email
 * @param password - User password
 * @returns Auth result with user and token
 * @throws Error if registration fails
 */
export async function registerWithEmail(
  email: string,
  password: string
): Promise<AuthResult> {
  const auth = getFirebaseAuth()
  const credential = await createUserWithEmailAndPassword(auth, email, password)
  return processCredential(credential)
}

/**
 * Login with Google OAuth
 *
 * @returns Auth result with user and token
 * @throws Error if login fails
 */
export async function loginWithGoogle(): Promise<AuthResult> {
  const auth = getFirebaseAuth()
  const provider = new GoogleAuthProvider()
  const credential = await signInWithPopup(auth, provider)
  return processCredential(credential)
}

/**
 * Login with GitHub OAuth
 *
 * @returns Auth result with user and token
 * @throws Error if login fails
 */
export async function loginWithGithub(): Promise<AuthResult> {
  const auth = getFirebaseAuth()
  const provider = new GithubAuthProvider()
  const credential = await signInWithPopup(auth, provider)
  return processCredential(credential)
}

/**
 * Logout current user
 *
 * @throws Error if logout fails
 */
export async function logout(): Promise<void> {
  const auth = getFirebaseAuth()
  await firebaseSignOut(auth)
}

/**
 * Get current user's ID token
 *
 * @param forceRefresh - Force token refresh even if not expired
 * @returns ID token or null if no user
 */
export async function getCurrentUserToken(forceRefresh = false): Promise<string | null> {
  const auth = getFirebaseAuth()
  const user = auth.currentUser

  if (!user) {
    return null
  }

  return await getToken(user, forceRefresh)
}

/**
 * Refresh current user's token
 * Forces Firebase to generate a new ID token
 *
 * @returns New ID token or null if no user
 */
export async function refreshToken(): Promise<string | null> {
  return await getCurrentUserToken(true)
}

/**
 * Get current Firebase user
 *
 * @returns Firebase user or null if not authenticated
 */
export function getCurrentUser(): FirebaseUser | null {
  const auth = getFirebaseAuth()
  return auth.currentUser
}

/**
 * Send email verification to current user
 *
 * @throws Error if no user is logged in or send fails
 */
export async function sendVerificationEmail(): Promise<void> {
  const user = getCurrentUser()

  if (!user) {
    throw new Error('No user logged in')
  }

  // Note: You need to configure the action URL in Firebase Console:
  // Authentication → Templates → Customize action URL
  // Set it to: http://localhost:5173/auth/action (or your production domain)

  await sendEmailVerification(user)
}

/**
 * Check if current user's email is verified
 *
 * @returns true if email is verified, false otherwise
 */
export function isEmailVerified(): boolean {
  const user = getCurrentUser()
  return user?.emailVerified ?? false
}

/**
 * Send password reset email to user
 *
 * @param email - User email address
 * @throws Error if send fails
 */
export async function sendPasswordReset(email: string): Promise<void> {
  const auth = getFirebaseAuth()

  // Note: You need to configure the action URL in Firebase Console:
  // Authentication → Templates → Customize action URL
  // Set it to: http://localhost:5173/auth/action (or your production domain)
  // Firebase will add ?mode=resetPassword&oobCode=xxx automatically

  await sendPasswordResetEmail(auth, email)
}

/**
 * Verify password reset code validity
 *
 * @param code - Password reset code from email
 * @returns Email address associated with the code
 * @throws Error if code is invalid or expired
 */
export async function verifyPasswordResetCodeValid(code: string): Promise<string> {
  const auth = getFirebaseAuth()
  return await verifyPasswordResetCode(auth, code)
}

/**
 * Reset password with code from email
 *
 * @param code - Password reset code from email
 * @param newPassword - New password to set
 * @throws Error if reset fails
 */
export async function resetPassword(code: string, newPassword: string): Promise<void> {
  const auth = getFirebaseAuth()
  await confirmPasswordReset(auth, code, newPassword)
}

/**
 * Send magic link (passwordless) sign-in email
 *
 * @param email - User email address
 * @throws Error if send fails
 */
export async function sendMagicLink(email: string): Promise<void> {
  const auth = getFirebaseAuth()

  // ActionCodeSettings for magic link
  const actionCodeSettings = {
    // URL to redirect to after user clicks the link
    url: `${window.location.origin}/auth/magic-link`,
    // This must be true for magic link sign-in
    handleCodeInApp: true,
  }

  await sendSignInLinkToEmail(auth, email, actionCodeSettings)

  // Save the email locally so we can complete sign-in later
  // (Firebase requires the email to complete the sign-in)
  window.localStorage.setItem('emailForSignIn', email)
}

/**
 * Check if the current URL is a magic link sign-in callback
 *
 * @returns true if this is a magic link callback
 */
export function isMagicLinkCallback(): boolean {
  const auth = getFirebaseAuth()
  return isSignInWithEmailLink(auth, window.location.href)
}

/**
 * Complete magic link sign-in
 *
 * @param email - User email (optional, will try to retrieve from localStorage)
 * @returns Auth result with user and token
 * @throws Error if sign-in fails
 */
export async function completeMagicLinkSignIn(email?: string): Promise<AuthResult> {
  const auth = getFirebaseAuth()

  // Get email from parameter or localStorage
  let userEmail = email
  if (!userEmail) {
    userEmail = window.localStorage.getItem('emailForSignIn')
  }

  if (!userEmail) {
    throw new Error('Email not found. Please provide your email to complete sign-in.')
  }

  // Sign in with the email link
  const credential = await signInWithEmailLink(auth, userEmail, window.location.href)

  // Clear the email from storage
  window.localStorage.removeItem('emailForSignIn')

  return processCredential(credential)
}